<template>
  <div class="image-cell-wrapper">
    <template v-if="imgSrc">
      <el-popover
          placement="right"
          trigger="hover"
          width="300"
          :open-delay="200"
          popper-class="image-popover"
          @show="handlePopoverShow"
      >
        <!-- 大图预览 - 鼠标移上去才加载 -->
        <div class="preview-container">
          <el-image
              v-if="showPreview"
              :src="compressedImg(imgSrc, 400)"
              :preview-src-list="previewSrcList"
              style="width:280px;height:370px"
              fit="contain"
              class="preview-image"
          >
            <div slot="placeholder" class="img-placeholder">
              <i class="el-icon-loading" style="font-size:24px;"></i>
              <span style="margin-top:8px;">加载中...</span>
            </div>
            <div slot="error" class="img-error">
              <i class="el-icon-picture-outline" style="font-size:28px;"></i>
              <span style="margin-top:4px;">加载失败</span>
            </div>
          </el-image>
          <!-- 图片数量角标 -->
          <div v-if="imgCount > 1" class="image-count-badge">
            +{{ imgCount - 1 }}
          </div>
        </div>

        <!-- 缩略图 - 只加载160px -->
        <div slot="reference" class="thumbnail-wrapper">
          <el-image
              :src="compressedImg(imgSrc, 160)"
              fit="contain"
              class="thumbnail-image"
          >
            <div slot="placeholder" class="img-placeholder-small">
              <i class="el-icon-loading"></i>
            </div>
            <div slot="error" class="img-error-small">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>
          <!-- 多图标识 -->
          <div v-if="imgCount > 1" class="multi-image-indicator">
            <i class="el-icon-document-copy"></i>
          </div>
        </div>
      </el-popover>
    </template>
    <span v-else class="no-image-text">
      <i class="el-icon-picture-outline"></i> 无图片
    </span>
  </div>
</template>

<script>
export default {
  name: 'ImageCell',
  props: {
    imgSrc: {
      type: String,
      default: ''
    },
    previewList: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      showPreview: false
    };
  },
  computed: {
    imgCount() {
      if (!this.imgSrc) return 0;
      return this.imgSrc.split(',').filter(url => url.trim()).length;
    },
    // 预览图列表：优先使用外部传入，否则默认取当前 imgSrc 的全部图
    previewSrcList() {
      if (this.previewList && this.previewList.length) {
        return this.previewList;
      }
      return this.srcPreviewList(this.imgSrc);
    }
  },
  methods: {
    /**
     * 获取保持原始比例的压缩图片URL
     * @param {string} src - 图片地址
     * @param {number} size - 限制的长边尺寸（宽度或高度）
     * @returns {string} 压缩后的图片URL
     */
    compressedImg(src, size = 200) {
      if (!src) return '';
      const firstImg = src.split(',')[0].trim();
      if (!firstImg) return '';
      if (firstImg.includes('x-tos-process=image/resize')) {
        return firstImg;
      }
      const separator = firstImg.includes('?') ? '&' : '?';
      // 使用 lfit（等比缩放），只限制长边，保持原始比例
      return `${firstImg}${separator}x-tos-process=image/resize,l_${size},m_lfit`;
    },
    srcPreviewList(src) {
      if (!src) return [];
      return src.split(',')
          .filter(url => url.trim())
          .map(url => url.trim());
    },
    // 鼠标移上去才加载大图
    handlePopoverShow() {
      if (!this.showPreview) {
        this.$nextTick(() => {
          this.showPreview = true;
        });
      }
    }
  }
}
</script>

<style scoped>
.image-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  padding: 4px 0;
}

/* ===== 缩略图容器 - 完全自适应 ===== */
.thumbnail-wrapper {
  position: relative;
  height: 88px;
  max-width: 160px;
  min-width: 48px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #f7f8fc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.thumbnail-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.25);
}

.thumbnail-wrapper:hover .thumbnail-image {
  filter: brightness(1.05);
}

.thumbnail-image {
  height: 100%;
  width: auto;
  max-width: 100%;
  display: block;
  transition: filter 0.3s ease;
  object-fit: contain;
}

/* ===== 缩略图占位/错误 ===== */
.img-placeholder-small {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 64px;
  color: #c0c4cc;
  font-size: 20px;
  background: #f7f8fc;
  flex-shrink: 0;
}

.img-error-small {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 64px;
  color: #d4a0a0;
  font-size: 24px;
  background: #fdf6f6;
  flex-shrink: 0;
}

/* ===== 多图角标（缩略图上） ===== */
.multi-image-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 10px;
  backdrop-filter: blur(4px);
  line-height: 1.6;
  pointer-events: none;
}

.multi-image-indicator i {
  font-size: 10px;
}

/* ===== 无图片状态 ===== */
.no-image-text {
  color: #bfbfbf;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-image-text i {
  font-size: 16px;
}

/* ===== Popover 内部预览 ===== */
.preview-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fafbfc;
  border-radius: 8px;
  padding: 8px;
  min-height: 370px;
}

.preview-image {
  border-radius: 6px;
  background: #fff;
}

/* ===== 图片数量角标（预览图右上角） ===== */
.image-count-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 2px 12px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

/* ===== 预览占位/错误 ===== */
.img-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;
  background: #f7f8fc;
}

.img-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #d4a0a0;
  background: #fdf6f6;
}
</style>

<!-- 全局样式：用于 Popover 本身 -->
<style>
.image-popover {
  padding: 12px !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15) !important;
}
.image-popover .el-popover__title {
  margin-bottom: 8px;
}
</style>